import {
  type JSX,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import axios from 'axios';

import { BootStatus } from '../components/core/BootStatus';

export type Configuration = {
  environment?: string;
  firebaseConfig?: {
    apiKey: string;
    authDomain: string;
    databaseURL: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
  };
  firebaseCloudMessaging?: {
    vapidKey: string;
  };
  appCheck?: {
    siteKey: string;
    debugToken?: string;
  };
  app?: {
    apiUrl: string;
    pageUrl: string;
    appUrl: string;
    filesUrl: string;
  };
};

interface ConfigurationProps {
  configuration: Configuration;
}

export const ConfigurationContext = createContext<ConfigurationProps | null>(
  null,
);

export const ConfigurationContextProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [configuration, setConfiguration] = useState<Configuration>();

  // Distinct from `configuration === undefined`, which is the state this shares with
  // "still loading". Without it a failed fetch is indistinguishable from a slow one
  // and the splash stays up for good, since nothing here retries on its own.
  const [failed, setFailed] = useState(false);

  const fetchConfiguration = useCallback(async () => {
    setFailed(false);

    try {
      const response = await axios.get('/config/env.json');

      if (response && response.status === 200 && response.data !== undefined) {
        setConfiguration(response.data);
        return;
      }
    } catch {
      // Falls through to the failure below, which a 2xx carrying no body reaches
      // too: every context beneath this one reads fields off the configuration and
      // would fail one by one instead.
    }

    setFailed(true);
  }, []);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  if (configuration === undefined) {
    return (
      <BootStatus
        failed={failed}
        message="No hemos podido cargar la configuración de la aplicación. Comprueba tu conexión e inténtalo de nuevo."
        onRetry={fetchConfiguration}
      />
    );
  }

  return (
    <ConfigurationContext.Provider value={{ configuration }}>
      {children}
    </ConfigurationContext.Provider>
  );
};

export const useConfiguration = () => {
  const context = useContext(ConfigurationContext);

  if (!context) {
    throw new Error(
      'useConfiguration must be used within a ConfigurationContextProvider',
    );
  }

  return context;
};
